/**
 * Security center (Phase J).
 *
 * - Header with current `security_profile`, target (if mid-escalation),
 *   `authorization_key_status`, last rotation date.
 * - Active op state (with auto-poll while non-terminal).
 * - Certificate inventory (issuer, valid until, status).
 * - Last 50 security events.
 * - Action buttons: rotate (disabled with tooltip when 503), escalate.
 *
 * Polls `getSecurityOverview` every 12s while a security op is in flight.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';

import OcppActionButton from '../components/OcppActionButton';
import OcppConfirmDialog from '../components/OcppConfirmDialog';
import OcppEmptyState from '../components/OcppEmptyState';
import OcppErrorBanner from '../components/OcppErrorBanner';
import OcppKvList from '../components/OcppKvList';
import OcppLoadingState from '../components/OcppLoadingState';
import OcppSectionCard from '../components/OcppSectionCard';
import OcppStatusPill from '../components/OcppStatusPill';
import { OcppTheme } from '../components/OcppTheme';
import { usePolledResource } from '../hooks/usePolledResource';
import {
  escalateSecurityProfile,
  getSecurityOverview,
  rotateAuthorizationKey,
} from '../services';
import { isOcppSuccess, mapOcppApiError, type OcppApiErrorInfo } from '../utils/apiErrors';
import {
  formatOcppTimestamp,
  securityOpStateLabel,
  securitySeverityColor,
} from '../utils/statusFormat';
import {
  isSecurityOpInFlight,
  type SecurityOp,
  type SecurityOverview,
  type SecurityProfile,
} from '../models/security';
import type { OcppTabProps } from './types';

const ACTIVE_POLL_MS = 12_000;
const IDLE_POLL_MS = 0;

const profileColor = (p: number | SecurityProfile): string => {
  if (p === 3) return OcppTheme.success;
  if (p === 2) return OcppTheme.accent;
  return OcppTheme.warning;
};

const SecurityTab: React.FC<OcppTabProps> = ({ chargerId, isActive }) => {
  const fetcher = useCallback(() => getSecurityOverview(chargerId), [chargerId]);
  const [pollMs, setPollMs] = useState<number>(IDLE_POLL_MS);
  const { data, error, loading, reload } = usePolledResource<SecurityOverview>(
    fetcher,
    { intervalMs: pollMs, enabled: isActive }
  );

  const inFlightOp: SecurityOp | undefined = useMemo(() => {
    return data?.active_ops?.find((op) => isSecurityOpInFlight(op.state));
  }, [data?.active_ops]);

  React.useEffect(() => {
    setPollMs(inFlightOp ? ACTIVE_POLL_MS : IDLE_POLL_MS);
  }, [inFlightOp]);

  const [rotationKey, setRotationKey] = useState<string>('');
  const [pendingRotate, setPendingRotate] = useState(false);
  const [escalateTarget, setEscalateTarget] = useState<2 | 3>(2);
  const [pendingEscalate, setPendingEscalate] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [actionError, setActionError] = useState<OcppApiErrorInfo | null>(null);
  const [rotationGated, setRotationGated] = useState(false);

  const onRotate = useCallback(async () => {
    setBusyAction('rotate');
    setActionError(null);
    const res = await rotateAuthorizationKey(chargerId, { new_authorization_key: rotationKey });
    if (isOcppSuccess(res.status)) {
      setRotationKey('');
      await reload();
    } else {
      const info = mapOcppApiError(res.status, res.payload);
      if (info.featureGated) setRotationGated(true);
      setActionError(info);
    }
    setBusyAction(null);
  }, [chargerId, rotationKey, reload]);

  const onEscalate = useCallback(async () => {
    setBusyAction('escalate');
    setActionError(null);
    const res = await escalateSecurityProfile(chargerId, { target_profile: escalateTarget });
    if (isOcppSuccess(res.status)) {
      await reload();
    } else {
      setActionError(mapOcppApiError(res.status, res.payload));
    }
    setBusyAction(null);
  }, [chargerId, escalateTarget, reload]);

  if (loading) return <OcppLoadingState label="Loading security overview…" />;
  if (error) return <OcppErrorBanner error={error} onRetry={reload} />;
  if (!data) return <OcppEmptyState icon="security" title="No security data" />;

  const profile = data.security_profile;
  const target = data.security_profile_target;

  const rotateButtonDisabled =
    rotationKey.length < 16 ||
    rotationGated ||
    data.authorization_key_status === 'rotating' ||
    !!inFlightOp;

  return (
    <View>
      {actionError ? <OcppErrorBanner error={actionError} /> : null}

      <OcppSectionCard
        title="Security profile"
        subtitle={
          target && target !== profile
            ? `Currently profile ${profile} — escalation to ${target} in flight.`
            : `Operating at security profile ${profile}.`
        }
        trailing={
          <OcppStatusPill
            label={`Profile ${profile}`}
            color={profileColor(profile)}
          />
        }
      >
        <OcppKvList
          items={[
            { key: 'AuthorizationKey', value: data.authorization_key_status },
            {
              key: 'Last rotated',
              value: formatOcppTimestamp(data.authorization_key_last_rotated_at),
            },
            {
              key: 'Active op',
              value: inFlightOp
                ? `${inFlightOp.op_type} · ${securityOpStateLabel(inFlightOp.state)}`
                : 'None',
            },
          ]}
        />
      </OcppSectionCard>

      {inFlightOp ? (
        <OcppSectionCard
          title="Operation in flight"
          subtitle="Polling every 12 seconds. The page will update automatically."
        >
          <OcppKvList
            items={[
              { key: 'Type', value: inFlightOp.op_type },
              { key: 'State', value: securityOpStateLabel(inFlightOp.state) },
              { key: 'Started', value: formatOcppTimestamp(inFlightOp.started_at) },
              { key: 'Last error', value: inFlightOp.last_error ?? '—' },
            ]}
          />
        </OcppSectionCard>
      ) : null}

      <OcppSectionCard
        title="Rotate AuthorizationKey"
        subtitle={
          rotationGated
            ? 'Rotation is currently disabled by the operator. Contact support to enable OCPP_CSMS_ROTATION_READY.'
            : 'Provide a new key (≥ 16 characters). The op runs as pre-stage → push → reconnect.'
        }
      >
        <Text style={styles.fieldLabel}>New authorization key</Text>
        <TextInput
          value={rotationKey}
          onChangeText={setRotationKey}
          autoCapitalize="none"
          autoCorrect={false}
          secureTextEntry
          style={styles.input}
          placeholderTextColor={OcppTheme.textDim}
          placeholder="At least 16 characters"
        />
        <OcppActionButton
          label="Rotate key"
          icon="vpn-key"
          variant="danger"
          disabled={rotateButtonDisabled}
          loading={busyAction === 'rotate'}
          onPress={() => setPendingRotate(true)}
          style={{ marginTop: 12, alignSelf: 'flex-start' }}
        />
      </OcppSectionCard>

      <OcppSectionCard
        title="Escalate security profile"
        subtitle="Cannot downgrade. Profile 3 requires a charger-side certificate already installed."
      >
        <View style={styles.chipRow}>
          {[2, 3].map((p) => (
            <OcppActionButton
              key={p}
              label={`Profile ${p}`}
              variant={escalateTarget === p ? 'primary' : 'ghost'}
              onPress={() => setEscalateTarget(p as 2 | 3)}
              style={styles.profileChip}
            />
          ))}
        </View>
        <OcppActionButton
          label={`Escalate → profile ${escalateTarget}`}
          icon="arrow-upward"
          disabled={escalateTarget <= profile || !!inFlightOp}
          loading={busyAction === 'escalate'}
          onPress={() => setPendingEscalate(true)}
          style={{ marginTop: 12, alignSelf: 'flex-start' }}
        />
      </OcppSectionCard>

      <OcppSectionCard
        title="Certificate inventory"
        subtitle={`${data.certificates.length} certificate(s) on charger.`}
      >
        {data.certificates.length === 0 ? (
          <OcppEmptyState icon="verified" title="No certificates installed" />
        ) : (
          data.certificates.map((c) => (
            <View key={c.id} style={styles.certRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.certType}>{c.certificate_type}</Text>
                <Text style={styles.certMeta}>
                  serial {c.serial_number?.slice(0, 16) ?? '—'}
                  {' · '}
                  expires {formatOcppTimestamp(c.not_after)}
                </Text>
              </View>
              <OcppStatusPill
                label={c.deleted_at ? 'deleted' : 'active'}
                color={c.deleted_at ? OcppTheme.danger : OcppTheme.success}
                size="sm"
              />
            </View>
          ))
        )}
      </OcppSectionCard>

      <OcppSectionCard title="Recent events" subtitle="Last 50 security events for this charger.">
        {data.recent_events.length === 0 ? (
          <OcppEmptyState icon="security" title="No security events" />
        ) : (
          data.recent_events.map((evt) => (
            <View key={evt.id} style={styles.eventRow}>
              <View
                style={[styles.dot, { backgroundColor: securitySeverityColor(evt.severity) }]}
              />
              <View style={{ flex: 1 }}>
                <Text style={styles.eventTitle}>{evt.event_type}</Text>
                <Text style={styles.eventMeta}>
                  {formatOcppTimestamp(evt.received_at)}
                  {evt.tech_info ? ` · ${evt.tech_info}` : ''}
                </Text>
              </View>
              <OcppStatusPill
                label={evt.severity}
                color={securitySeverityColor(evt.severity)}
                size="sm"
              />
            </View>
          ))
        )}
      </OcppSectionCard>

      <OcppConfirmDialog
        visible={pendingRotate}
        title="Rotate AuthorizationKey?"
        message={`The new key (${rotationKey.length} chars) will be pre-staged on the CSMS and pushed to the charger. The charger will reconnect with the new key. You cannot reverse this without rotating again.`}
        confirmLabel="Rotate"
        confirmVariant="danger"
        loading={busyAction === 'rotate'}
        onCancel={() => setPendingRotate(false)}
        onConfirm={async () => {
          setPendingRotate(false);
          await onRotate();
        }}
      />

      <OcppConfirmDialog
        visible={pendingEscalate}
        title={`Escalate to profile ${escalateTarget}?`}
        message="Profile escalation will trigger a charger reconnect under the new TLS / cert posture. Make sure the certificate inventory contains everything required."
        confirmLabel="Escalate"
        loading={busyAction === 'escalate'}
        onCancel={() => setPendingEscalate(false)}
        onConfirm={async () => {
          setPendingEscalate(false);
          await onEscalate();
        }}
      />
    </View>
  );
};

export default SecurityTab;

const styles = StyleSheet.create({
  fieldLabel: {
    color: OcppTheme.textMuted,
    fontSize: 12,
    marginBottom: 4,
  },
  input: {
    backgroundColor: OcppTheme.surfaceAlt,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: OcppTheme.border,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: OcppTheme.textPrimary,
    fontSize: 14,
  },
  chipRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
  },
  profileChip: {
    minHeight: 32,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  certRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: OcppTheme.divider,
  },
  certType: {
    color: OcppTheme.textPrimary,
    fontSize: 13,
    fontWeight: '600',
  },
  certMeta: {
    color: OcppTheme.textMuted,
    fontSize: 11,
    marginTop: 2,
  },
  eventRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    gap: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: OcppTheme.divider,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  eventTitle: {
    color: OcppTheme.textPrimary,
    fontSize: 13,
    fontWeight: '600',
  },
  eventMeta: {
    color: OcppTheme.textMuted,
    fontSize: 11,
    marginTop: 2,
  },
});
